import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import NProgress from 'nprogress';
import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';

import Trash from '@/assets/icons/trash.svg';
import ConfirmModalButtons from '@/components/app/confirmModalButtons/ConfirmModalButtons';
import { googleMapsSelector } from '@/components/state/GoogleMapsState';
import { PolygonIris } from '@/components/zoning/interfaces';
import { getPolygonsIris, PARIS } from '@/components/zoning/utils';
import {
  DEFAULT_ZOOM,
  STYLE_MAP,
  STYLE_POLYGON_AREA
} from '@/constants/zoning';
import { useCurrentAgent } from '@/hooks/agents/useCurrentAgent';
import { EventPageView, PAGE_VIEW_TITLE, sendPageView } from '@/utils/gtm';
import { logError } from '@/utils/log';
import { toastError } from '@/utils/notification';
import { generateButtonId } from '@/utils/tracking';
import polyline from '@mapbox/polyline';
import { generateLatLng, nullOrString } from '@proprioo/hokkaido';
import { Button, ButtonSizes, ButtonTheme, useModal } from '@proprioo/salatim';
import { GoogleMap, Polygon } from '@react-google-maps/api';

import CriteriaSelection from '../criteriaSelection/CriteriaSelection';
import { LocationState } from '../interfaces';
import {
  MapActions,
  MapDelete,
  MapWarning,
  MapWrapper
} from './CriteriaMap.styles';
import { getAreas } from './fetch';
import {
  cleanDuplicatedPolygons,
  filterArea,
  OPTIONS_MAP,
  STYLE_POLYGON_AREA_SELECTED
} from './utils';

type CriteriaMapProps = {
  locations: LocationState[];
  hasDeleteModal?: boolean;
  update(locations: LocationState[], isMap: boolean): void;
};

type PolygonArea = PolygonIris & {
  isSelected: boolean;
};

type Config = {
  view: nullOrString;
  zoom: number;
};

const CriteriaMap: FC<CriteriaMapProps> = ({
  hasDeleteModal = true,
  locations,
  update
}) => {
  const { asPath } = useRouter();
  const { t } = useTranslation();

  const { createModal, openModal, closeModal } = useModal({
    onOpen: () =>
      sendPageView(
        { path: asPath, title: PAGE_VIEW_TITLE.MODAL_BUYER_MAP_RESET },
        EventPageView.MODAL_OPENING
      )
  });

  const isGoogleMapsLoaded = useRecoilValue(googleMapsSelector);
  const currentAgent = useCurrentAgent();

  const [config, setConfig] = useState<Config>({
    view: null,
    zoom: DEFAULT_ZOOM
  });
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [areas, setAreas] = useState<LocationState[]>(locations);
  const [selectedAreas, setSelectedAreas] = useState<LocationState[]>(
    filterArea<LocationState>(locations)
  );

  const onLoad = useCallback((map: google.maps.Map) => setMap(map), []);
  const onUnmount = useCallback(() => setMap(null), []);

  const toggleArea = (areaId: string) => {
    const findPolygon = areas.filter(({ id }) => areaId === id);
    const findArea = selectedAreas.filter(({ id }) => areaId === id);
    const filteredAreas = selectedAreas.filter(({ id }) => areaId !== id);

    if (findArea.length) {
      setSelectedAreas(filteredAreas);
      update(filteredAreas, true);
    } else {
      setSelectedAreas([...filteredAreas, ...findPolygon]);
      update([...filteredAreas, ...findPolygon], true);
    }
  };

  const getZones = () => {
    const bounds = map?.getBounds();

    if (bounds && map) {
      const zoom = map.getZoom();

      if (zoom && zoom > 11) {
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const view: [number, number][] = [
          [ne.lat(), ne.lng()],
          [sw.lat(), ne.lng()],
          [sw.lat(), sw.lng()],
          [ne.lat(), sw.lng()],
          [ne.lat(), ne.lng()]
        ];
        setConfig({ view: polyline.encode(view), zoom });
      } else if (zoom) {
        setConfig({ view: null, zoom });
      }
    }
  };

  const fetchAreas = async (poly: string, signal: AbortSignal) => {
    try {
      const fetchAreas = await getAreas(poly, signal);
      const aggreatedAreas = cleanDuplicatedPolygons([
        ...(areas || []),
        ...(fetchAreas || [])
      ]);

      setAreas(filterArea(aggreatedAreas));
    } catch (error) {
      logError('Failed to fetch areas (buyerQualification)', { error, poly });
      toastError('retrievingLocationsError');
    } finally {
      NProgress.done();
    }
  };

  const deleteLocations = () => update([], false);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    const { view } = config;

    if (view) {
      NProgress.start();
      fetchAreas(view, signal);
    }

    return () => controller.abort();
  }, [config.view]);

  useEffect(() => {
    if (map) {
      if (locations.length) {
        const bounds = new google.maps.LatLngBounds();
        locations.forEach(({ geom }) => {
          const [polygon] = geom.flat();
          const geoJson = polyline.decode(polygon);
          const paths = generateLatLng(geoJson);
          paths.forEach(({ lat, lng }) => bounds.extend({ lat, lng }));
        });

        return map.fitBounds(bounds);
      } else if (currentAgent?.id) {
        return map.setCenter({
          lat: currentAgent.latitude,
          lng: currentAgent.longitude
        });
      }

      return map.setCenter({ lat: PARIS.lat, lng: PARIS.lng });
    }
  }, [map, currentAgent?.id]);

  const polygons: PolygonArea[] = useMemo(
    () =>
      getPolygonsIris(areas).map(area => ({
        ...area,
        isSelected: Boolean(
          selectedAreas.filter(({ id }) => id.includes(area.id)).length
        )
      })),
    [areas, selectedAreas]
  );

  const { zoom } = config;
  const isZoomNotEnough = Boolean(zoom && zoom <= 11);

  return (
    <Fragment>
      <MapActions>
        <CriteriaSelection areas={selectedAreas} removeArea={toggleArea} />
        {Boolean(polygons.filter(({ isSelected }) => isSelected).length) && (
          <MapDelete>
            <Button
              id={generateButtonId(
                PAGE_VIEW_TITLE.QUALIFICATION_BUYER_CREATE,
                'deleteCriteriaMap'
              )}
              icon={<Trash />}
              label={t('deleteNeighborhoodSelection')}
              rounded={true}
              size={ButtonSizes.SMALL}
              theme={ButtonTheme.RED}
              onClick={() => (hasDeleteModal ? openModal() : deleteLocations())}
            />
          </MapDelete>
        )}
      </MapActions>
      <MapWrapper data-test={`map-loaded-${isGoogleMapsLoaded}`}>
        {isZoomNotEnough && (
          <MapWarning>{t('zoomToSeeNeighborhood')}</MapWarning>
        )}
        <GoogleMap
          id="google-map"
          zoom={zoom}
          options={{ ...OPTIONS_MAP, disableDefaultUI: false }}
          mapContainerStyle={STYLE_MAP}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onDragEnd={getZones}
          onZoomChanged={getZones}
        >
          {polygons.map(({ id, geom, isSelected }) => (
            <Polygon
              paths={geom}
              key={id}
              onClick={() => toggleArea(id)}
              options={
                isSelected
                  ? STYLE_POLYGON_AREA_SELECTED
                  : {
                      ...STYLE_POLYGON_AREA,
                      clickable: !isZoomNotEnough,
                      visible: !isZoomNotEnough
                    }
              }
            />
          ))}
        </GoogleMap>
      </MapWrapper>
      {createModal({
        children: (
          <ConfirmModalButtons
            idAccept={generateButtonId(
              PAGE_VIEW_TITLE.MODAL_BUYER_MAP_RESET,
              'yes'
            )}
            idReject={generateButtonId(
              PAGE_VIEW_TITLE.MODAL_BUYER_MAP_RESET,
              'no'
            )}
            onAccept={deleteLocations}
            onReject={closeModal}
          />
        ),
        isAlternative: true,
        text: t('resetNeighborhoodSelectionConfirmation'),
        title: t('resetNeighborhoodSelection')
      })}
    </Fragment>
  );
};

export default CriteriaMap;
